<script setup>
import {RouterLink} from 'vue-router';
import AvatarImageLoader from "@/components/loaders/AvatarImageLoader.vue";

// Props
const props = defineProps({
  imageFileName: {
    type: String,
    default: null
  },
  icon: {
    type: String,
    default: null
  },
  title: {
    type: String,
    default: null
  },
  subtitle: {
    type: String,
    default: null
  },
  loading: {
    type: Boolean,
    default: false
  },
  click: {
    type: Function,
    default: () => null
  },
  to : {
    type : Object,
    default : null
  },
  roundedImage: {
    type: Boolean,
    default: false
  },
  // Icon-tile colour by competition type (mirrors Flutter TlChipKind /_CompKind):
  // league = red, ladder = blue, tournament = amber, meet = green,
  // americano = teal, neutral = grey.
  tone: {
    type: String,
    default: 'league',
    validator: (v) => ['league', 'ladder', 'tournament', 'meet', 'americano', 'neutral'].includes(v),
  }
});

// A cell without a route is click-only ("Message Sam"). It must not become a
// RouterLink: given a null target the production render dies with "Cannot read
// properties of null (reading 'path')", which 500'd every fixture page that
// showed one (2026-10-09).
const onClick = () => {
  if (!props.to) props.click?.();
};
</script>

<template>
  <!-- comp-row (mockups/redesign/club.html): icon tile + name/sub + trailing chip + chevron -->
  <component :is="to ? RouterLink : 'div'" :to="to || undefined" :role="to ? undefined : 'button'"
             class="comp-row tl-card" @click="onClick">
    <div class="comp-icon" :class="`comp-icon--${tone}`">
      <slot name="icon">
        <avatar-image-loader
            v-if="imageFileName"
            :image-file-name="imageFileName"
            :image-clickable="false"
            size="40"
            class="comp-icon-img"
        />
        <v-icon v-else-if="icon" size="22" :icon="icon" />
      </slot>
    </div>

    <div class="comp-who">
      <slot name="title">
        <div class="comp-title">{{ title }}</div>
      </slot>
      <slot name="subtitle">
        <div v-if="subtitle" class="comp-sub">{{ subtitle }}</div>
      </slot>
    </div>

    <div class="comp-trailing">
      <slot name="trailing" />
      <v-icon icon="mdi-chevron-right" size="20" class="comp-chev" />
    </div>
  </component>
</template>

<style scoped lang="scss">
.comp-row {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 13px 14px;
  margin-bottom: 10px;
  text-decoration: none;
  color: rgb(var(--v-theme-text));
  transition: border-color 0.15s ease;

  &:hover {
    border-color: rgb(var(--v-theme-textSubtle));
  }
}

.comp-icon {
  width: 40px;
  height: 40px;
  border-radius: 11px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  overflow: hidden;
}

/* competition-type tile colours (Flutter _CompKind / TlChipKind) */
.comp-icon--league {
  background: rgb(var(--v-theme-brandTint));
  color: rgb(var(--v-theme-primary));
}

.comp-icon--ladder {
  background: #E8F1FB;
  color: #2477C8;
}

.comp-icon--tournament {
  background: rgb(var(--v-theme-warningTint));
  color: rgb(var(--v-theme-warning));
}

.comp-icon--meet {
  background: rgb(var(--v-theme-successTint));
  color: rgb(var(--v-theme-success));
}

/* americano purple — same pair everywhere (#F0EAFB tint / #7C3AED ink) */
.comp-icon--americano {
  background: #F0EAFB;
  color: #7C3AED;
}

.comp-icon--neutral {
  background: rgb(var(--v-theme-bg));
  color: rgb(var(--v-theme-charcoal));
}

.comp-icon-img {
  border-radius: 11px !important;
}

.comp-who {
  min-width: 0;
  flex: 1 1 auto;
}

.comp-title {
  font-size: 15px;
  font-weight: 700;
  letter-spacing: -0.2px;
  line-height: 1.25;
  overflow: hidden;
  text-overflow: ellipsis;
}

.comp-sub {
  font-size: 12.5px;
  color: rgb(var(--v-theme-textSubtle));
  margin-top: 2px;
}

.comp-trailing {
  display: flex;
  align-items: center;
  gap: 8px;
  flex-shrink: 0;
}

.comp-chev {
  color: rgb(var(--v-theme-textSubtle));
}
</style>
